// Gmail message content

// Demo messages - sequence for auto demo
export const demoMessages = [
  "I'd like to schedule a product demo next week",
  "What's your availability for a call to discuss pricing options?",
  "Can you send me more information about your enterprise plan?",
  "Need to reschedule our meeting from Tuesday to Thursday",
  "Are there any discounts available for annual subscriptions?"
];

// Predefined tool responses for each demo message
export const demoResponses: Record<string, {tool: string, input: any, output: any, subject: string}> = {
  "I'd like to schedule a product demo next week": {
    tool: 'calendar',
    subject: 'Product Demo Scheduled',
    input: { 
      action: "create_event",
      attendees: ["customer"],
      date: "next week",
      time: "2:00 PM", 
      duration: 60
    },
    output: { 
      event_id: "evt_456789",
      title: "Product Demo with Sales Team",
      date: "2024-06-12",
      time: "2:00 PM - 3:00 PM",
      attendees: [
        "sarah@customer.com",
        "michael@ourcompany.com",
        "jennifer@ourcompany.com"
      ],
      location: "Zoom Video Conference",
      meeting_link: "https://zoom.us/j/123456789",
      status: "confirmed"
    }
  },
  "What's your availability for a call to discuss pricing options?": {
    tool: 'check_availability',
    subject: 'Available Times for Pricing Discussion',
    input: { 
      sales_rep: "michael_johnson",
      week: "current",
      meeting_type: "pricing_discussion"
    },
    output: { 
      available_slots: [
        { date: "2024-06-10", time: "10:00 AM", duration: 30 },
        { date: "2024-06-10", time: "3:30 PM", duration: 30 },
        { date: "2024-06-11", time: "11:00 AM", duration: 30 },
        { date: "2024-06-12", time: "2:00 PM", duration: 30 }
      ],
      sales_rep: "Michael Johnson",
      timezone: "Eastern Time (ET)",
      booking_link: "https://calendly.com/michael-sales/pricing",
    }
  },
  "Can you send me more information about your enterprise plan?": {
    tool: 'send_materials',
    subject: 'Enterprise Plan Information',
    input: { 
      material_type: "product_info",
      plan: "enterprise",
      format: "pdf"
    },
    output: { 
      materials: [
        { name: "Enterprise Plan Overview", type: "brochure", size: "2.4 MB" },
        { name: "Enterprise Case Studies", type: "case_studies", size: "3.7 MB" },
        { name: "Enterprise Pricing Guide", type: "pricing", size: "1.2 MB" },
        { name: "Implementation Timeline", type: "timeline", size: "1.8 MB" }
      ],
      follow_up: {
        type: "call",
        recommended_date: "within 3 business days"
      },
      delivery_method: "email_attachment",
      status: "sent"
    }
  },
  "Need to reschedule our meeting from Tuesday to Thursday": {
    tool: 'update_meeting',
    subject: 'Meeting Rescheduled to Thursday',
    input: { 
      current_date: "Tuesday, June 11, 2024",
      new_date: "Thursday, June 13, 2024",
      keep_time: true,
      meeting_id: "meet_123456"
    },
    output: { 
      meeting_id: "meet_123456",
      old_schedule: {
        date: "Tuesday, June 11, 2024",
        time: "1:00 PM - 2:00 PM",
      },
      new_schedule: {
        date: "Thursday, June 13, 2024", 
        time: "1:00 PM - 2:00 PM",
      },
      attendees_notified: [
        "john@customer.com",
        "alice@customer.com",
        "michael@ourcompany.com"
      ],
      calendar_updated: true,
      status: "rescheduled"
    }
  },
  "Are there any discounts available for annual subscriptions?": {
    tool: 'pricing_info',
    subject: 'Annual Subscription Discount Information',
    input: { 
      pricing_type: "subscription",
      plan_period: "annual",
      discount_inquiry: true
    },
    output: { 
      discount_available: true,
      discount_percentage: "20%",
      plans: [
        { name: "Basic", monthly: "$49", annual: "$470", savings: "$118" },
        { name: "Professional", monthly: "$99", annual: "$950", savings: "$238" },
        { name: "Enterprise", monthly: "$299", annual: "$2,870", savings: "$718" }
      ],
      promotion_code: "ANNUAL20",
      expires: "2024-07-31",
      additional_benefits: [
        "Priority customer support",
        "Free onboarding session",
        "Quarterly business review"
      ]
    }
  }
};

// Response templates for each tool type
export const responseTemplates: Record<string, string> = {
  calendar: `I've scheduled your product demo! 📅 👥 🚀\n\n📋 Meeting Details:\n• 📝 Title: {title}\n• 📆 Date: {date}\n• 🕒 Time: {time}\n• 📍 Location: {location}\n• 🔗 Meeting Link: {meeting_link}\n• 👥 Attendees: {attendees.length} participants confirmed\n\n✉️ Calendar invites have been sent to all attendees.\n\n👉 Our product specialist will walk you through our solution and answer any questions you might have. Is there any specific feature you'd like us to focus on during the demo?`,
  
  check_availability: `Here are the available times to discuss pricing options with our sales team! 📆 💰\n\n🧑‍💼 Sales Representative: {sales_rep}\n🌐 Timezone: {timezone}\n\n<table class="avail-table" style="width:100%; border-collapse:collapse; margin:8px 0; font-size:11px;">
  <tr style="background:#f0f4f9;">
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Date</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Time</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Duration</th>
  </tr>
  {available_slots}
</table>\n\n🔗 You can book directly using this link: {booking_link}\n\n👉 Would you like me to reserve any of these times for you, or do you prefer to use the booking link?`,
  
  send_materials: `I've sent you the information about our Enterprise Plan! 📊 💼 ✨\n\n<table class="materials-table" style="width:100%; border-collapse:collapse; margin:8px 0; font-size:11px;">
  <tr style="background:#f0f4f9;">
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Material</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Type</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Size</th>
  </tr>
  {materials}
</table>\n\n📑 These documents provide comprehensive details about our Enterprise solution, including pricing, implementation, and case studies.\n\n👉 After reviewing the materials, would you like to schedule a call with our enterprise solutions specialist to discuss how we can customize the plan for your specific needs?`,
  
  update_meeting: `Your meeting has been successfully rescheduled! 🗓️ ✅\n\n<table class="schedule-table" style="width:100%; border-collapse:collapse; margin:8px 0; font-size:11px;">
  <tr style="background:#f0f4f9;">
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;"></th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Date</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Time</th>
  </tr>
  <tr>
    <td style="padding:4px 8px; border:1px solid #d0d5dd;"><strong>Previous</strong> ⏮️</td>
    <td style="padding:4px 8px; border:1px solid #d0d5dd;">{old_schedule_date}</td>
    <td style="padding:4px 8px; border:1px solid #d0d5dd;">{old_schedule_time}</td>
  </tr>
  <tr style="background:#e6f7e6;">
    <td style="padding:4px 8px; border:1px solid #d0d5dd;"><strong>New</strong> ✅</td>
    <td style="padding:4px 8px; border:1px solid #d0d5dd;">{new_schedule_date}</td>
    <td style="padding:4px 8px; border:1px solid #d0d5dd;">{new_schedule_time}</td>
  </tr>
</table>\n\n👥 All {attendees_notified_length} participants have been notified of this change.\n\n✅ Your calendar has been updated accordingly.\n\n👉 Is there anything else you need for this meeting? Perhaps I can help prepare an agenda or send reminders to the team?`,
  
  pricing_info: `Good news! We do offer discounts on annual subscriptions! 💰 💯\n\n🏷️ Annual Discount: {discount_percentage} off monthly price\n\n<table class="pricing-table" style="width:100%; border-collapse:collapse; margin:8px 0; font-size:11px;">
  <tr style="background:#f0f4f9;">
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Plan</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Monthly</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Annual</th>
    <th style="padding:4px 8px; text-align:left; border:1px solid #d0d5dd;">Savings</th>
  </tr>
  {plans}
</table>\n\n🎁 Promotion code: <span style="font-weight:bold; color:#0066cc;">{promotion_code}</span>\n⏰ Valid until: {expires}\n\n✨ Additional benefits with annual plans:\n{additional_benefits}\n\n👉 Would you like me to set up a call with our sales team to discuss which plan would best suit your needs?`
};

// Initial welcome email
export const welcomeEmail = {
  id: 1,
  sender: 'agent',
  from: 'Michael Johnson <michael@salesteam.com>',
  to: 'me',
  subject: 'Welcome - Your Personal Sales Assistant',
  text: 'Hello there! 👋\n\nI\'m Michael\'s AI assistant. Michael is currently your dedicated account manager at <a href="https://www.salescompany.com" target="_blank" style="color:#0066cc; text-decoration:underline;">SalesCompany</a>. I help Michael manage customer communications and scheduling.\n\nI can assist you with:\n- 📅 Scheduling product demos and sales calls\n- 💰 Providing pricing information and quotes\n- 📊 Sharing product materials and documentation\n- 🗓️ Managing meeting schedules and calendar coordination\n- ⏱️ Finding the best time slots based on availability\n- 🤝 Connecting you with the right specialist for your needs\n\nSimply reply to this email with your request, and I\'ll coordinate with Michael and the sales team to get you what you need quickly.\n\nLooking forward to helping you have a great experience with our products!\n\nBest regards,\nMichael\'s Assistant 🤖\nSales Team | <a href="https://www.salescompany.com" target="_blank" style="color:#0066cc; text-decoration:underline;">SalesCompany</a>',
  timestamp: new Date(Date.now() - 3600000),
};
